import { Button, Card, Flex, Typography, theme } from 'antd';
import { LeftOutlined, ReadOutlined } from '@ant-design/icons';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCfFlowEventTracker } from '../useCfFlowEventTracker';
import { useCfStepViewed } from '../useCfStepEvents';
import { CfBlockReason } from '../api/CfProbeResponse';
import { BLOCKED_SUB_CASE, CfEventSubject } from '../cfEventProperties';
import { guidesLocation } from '../integrationGuidesRoute';
import { useOpenNexusOnTheGuides } from '../useOpenNexusOnTheGuides';
import { NexusLogo } from '../../integrationWizard/components/steps/IntegrationStep/Nexus';
import CloudflareStepHeader from './CloudflareStepHeader';
import IntegrationPathOption from './IntegrationPathOption';

const { Title, Paragraph } = Typography;

export const CF_BLOCKED_EVENTS = {
  ctaClicked: 'Automated CF Integration: Blocked CTA Clicked',
} as const;

export type CloudflareBlockedStepProps = {
  blockReason: CfBlockReason | null;
  domain?: string;
  onGoBack?: () => void;
};

/**
 * The screen a customer sees when their domain cannot use the automated setup
 * (PRE-3290 / F2).
 *
 * Small, and it carries more weight than its size: for a blocked customer this
 * is the *entire* first experience of integrating. They can never reach the
 * beta's success path, so the screen's only job is to hand them the two paths
 * that do work.
 *
 * **The copy is generic and never names the platform we detected.** A signal is
 * evidence, not a diagnosis — the Akamai match in particular is best-effort —
 * and telling somebody "we detected Fastly" invites an argument we cannot win
 * when the guess is wrong. `block_reason` still travels, as telemetry, so the
 * beta can learn which blocks are common without asserting anything to the
 * customer.
 */
const CloudflareBlockedStep = ({
  blockReason,
  domain = '',
  onGoBack,
}: CloudflareBlockedStepProps) => {
  const { token } = theme.useToken();
  const subject: CfEventSubject = { domain, subCase: BLOCKED_SUB_CASE };
  const track = useCfFlowEventTracker(subject);
  useCfStepViewed('blocked', subject);
  const navigate = useNavigate();
  const location = useLocation();
  const openNexusOnTheGuides = useOpenNexusOnTheGuides();

  const onCta = (cta: 'nexus' | 'guide') => {
    track(CF_BLOCKED_EVENTS.ctaClicked, {
      cta,
      block_reason: blockReason ?? 'unknown',
    });

    if (cta === 'nexus') {
      openNexusOnTheGuides({ fromStep: 'blocked', ...(domain && { domain }), subject });

      return;
    }

    navigate(guidesLocation(location.search));
  };

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Analyzing your domain"
        description="We're checking your domain to find the best integration path."
      />

      <Card>
        <Flex gap={token.marginXL} wrap="wrap">
          <Flex vertical gap={token.marginMD} flex={1} style={{ minWidth: token.screenXS }}>
            <Title level={4} style={{ marginBottom: 0 }}>
              Automatic Cloudflare setup isn&apos;t available for your domain
            </Title>
            <Paragraph style={{ marginBottom: 0 }}>
              Based on the signals we detected, we can&apos;t set this domain up
              automatically through Cloudflare.
            </Paragraph>
            <Paragraph style={{ marginBottom: 0 }}>
              It may run on another CDN, use managed DNS, or be built on a
              no-code platform.
            </Paragraph>
            <Paragraph style={{ marginBottom: 0 }}>
              You can get this fully set up using Nexus or by following our
              step-by-step guides.
            </Paragraph>
          </Flex>

          <Flex
            vertical
            gap={token.marginSM}
            flex={1}
            style={{ minWidth: token.screenXS }}
          >
            <IntegrationPathOption
              icon={<NexusLogo size={token.sizeLG} />}
              title="Continue with Nexus"
              description="Let our AI Assistant walk you through it."
              onClick={() => onCta('nexus')}
            />
            <IntegrationPathOption
              icon={<ReadOutlined style={{ fontSize: token.fontSizeLG, color: token.colorText }} />}
              title="Follow the integration guides"
              description="Step-by-step guides for your stack."
              onClick={() => onCta('guide')}
            />
          </Flex>
        </Flex>
      </Card>

      {onGoBack && (
        <Flex>
          <Button type="link" icon={<LeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default CloudflareBlockedStep;
